
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Video, Clock, X, CheckCircle, Loader, User, Briefcase, Mail } from 'lucide-react';
import FadeIn from '../../components/FadeIn';
import SEO from '../../components/SEO';
import { db } from '../../firebase';
import { collection, query, orderBy, getDocs, addDoc } from 'firebase/firestore';

const Webinars = () => {
    const [events, setEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({ name: '', email: '', company: '', jobTitle: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                // Filter for future events or just all sorted by date
                const q = query(collection(db, 'events'), orderBy('date', 'asc'));
                const querySnapshot = await getDocs(q);
                const eventsData = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setEvents(eventsData);
            } catch (error) {
                console.error("Error fetching events:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchEvents();
    }, []);

    const handleRegisterClick = (event) => {
        setSelectedEvent(event);
        setShowModal(true);
        setIsSuccess(false);
        setFormData({ name: '', email: '', company: '', jobTitle: '' });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await addDoc(collection(db, 'attendees'), {
                ...formData,
                eventId: selectedEvent.id,
                eventTitle: selectedEvent.title,
                eventDate: selectedEvent.date,
                registeredAt: new Date().toISOString()
            });
            setIsSuccess(true);
            setTimeout(() => {
                setShowModal(false);
                setSelectedEvent(null);
            }, 3000);
        } catch (error) {
            console.error("Error registering:", error);
            alert("Registration failed. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-white">
            <SEO
                title="Webinars & Events | BPO Consultants"
                description="Join our live sessions on BPO trends, AI in sales, and global staffing strategies."
            />

            {/* Hero */}
            <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#0B0D15] overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img src="https://images.unsplash.com/photo-1544531886-983334309102?auto=format&fit=crop&q=80&w=1200" alt="Webinar" className="w-full h-full object-cover" />
                </div>
                <div className="max-w-7xl mx-auto relative z-10 text-center">
                    <FadeIn>
                        <span className="inline-block bg-[#A2CB52]/20 text-[#A2CB52] px-4 py-2 rounded-full text-sm font-bold border border-[#A2CB52]/40 mb-6 backdrop-blur-sm">
                            <Video size={14} className="inline mr-1" /> Live Insights
                        </span>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                            Webinars & Events
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            Learn from the experts. Stay ahead of the curve.
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* Upcoming Events */}
            <section className="py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-5xl mx-auto">
                    <FadeIn className="mb-12">
                        <h2 className="text-3xl font-bold text-[#20222D] mb-6">Upcoming Sessions</h2>
                    </FadeIn>

                    {isLoading ? (
                        <div className="text-center py-20 text-gray-500">Loading upcoming sessions...</div>
                    ) : events.length === 0 ? (
                        <div className="text-center py-20 bg-gray-50 rounded-2xl border border-gray-100">
                            <p className="text-xl font-bold text-gray-600 mb-2">No upcoming sessions scheduled.</p>
                            <p className="text-gray-500">Check back soon or subscribe to our newsletter for updates.</p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {events.map((event, i) => (
                                <FadeIn key={event.id} delay={i * 0.1}>
                                    <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all flex flex-col md:flex-row md:items-center justify-between gap-6">
                                        {event.image && (
                                            <div className="w-full md:w-48 h-32 flex-shrink-0 rounded-xl overflow-hidden bg-gray-100">
                                                <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
                                            </div>
                                        )}
                                        <div className="flex-1">
                                            <h3 className="text-xl font-bold text-[#20222D] mb-2">{event.title}</h3>
                                            <p className="text-gray-600 mb-4">{event.description}</p>
                                            <div className="flex items-center text-sm text-gray-500 space-x-4">
                                                <span className="flex items-center"><Calendar size={16} className="mr-2 text-[#A2CB52]" /> {event.date}</span>
                                                <span className="flex items-center"><Clock size={16} className="mr-2 text-[#A2CB52]" /> {event.time}</span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleRegisterClick(event)}
                                            className="bg-[#20222D] text-white px-6 py-3 rounded-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap self-start md:self-center"
                                        >
                                            Register for Free
                                        </button>
                                    </div>
                                </FadeIn>
                            ))}
                        </div>
                    )}

                    <FadeIn className="mt-20 text-center">
                        <p className="text-gray-500">
                            Want to watch previous sessions? <Link to="/contact" className="text-[#A2CB52] font-bold underline">Access the Archive</Link>
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* Registration Modal */}
            {showModal && selectedEvent && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-fade-in-up">
                        <div className="flex justify-between items-center p-6 border-b">
                            <h3 className="text-lg font-bold text-gray-900">Register for Event</h3>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-6">
                            {isSuccess ? (
                                <div className="text-center py-10">
                                    <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <CheckCircle size={32} />
                                    </div>
                                    <h4 className="text-xl font-bold text-gray-900 mb-2">Registration Confirmed!</h4>
                                    <p className="text-gray-600">You're all set for <strong>{selectedEvent.title}</strong>.</p>
                                    <p className="text-sm text-gray-500 mt-2">Check your email for details.</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="bg-gray-50 p-4 rounded-lg mb-4 text-sm">
                                        <p className="font-bold text-gray-900">{selectedEvent.title}</p>
                                        <p className="text-gray-500 flex items-center gap-2 mt-1">
                                            <Calendar size={12} /> {selectedEvent.date} at {selectedEvent.time}
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                                <User size={16} />
                                            </div>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                className="w-full pl-10 p-2 border rounded-lg focus:ring-2 focus:ring-brand-accent focus:outline-none"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                                <Mail size={16} />
                                            </div>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                className="w-full pl-10 p-2 border rounded-lg focus:ring-2 focus:ring-brand-accent focus:outline-none"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                                    <Briefcase size={16} />
                                                </div>
                                                <input
                                                    type="text"
                                                    name="company"
                                                    value={formData.company}
                                                    onChange={handleInputChange}
                                                    className="w-full pl-10 p-2 border rounded-lg focus:ring-2 focus:ring-brand-accent focus:outline-none"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                                            <input
                                                type="text"
                                                name="jobTitle"
                                                value={formData.jobTitle}
                                                onChange={handleInputChange}
                                                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-brand-accent focus:outline-none"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full bg-[#20222D] text-white py-3 rounded-xl font-bold hover:bg-gray-800 transition-all flex justify-center items-center gap-2 mt-2"
                                    >
                                        {isSubmitting ? <Loader className="animate-spin" size={18} /> : 'Complete Registration'}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Webinars;
